// src/components/Dropdown.js
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import arrowIcon from "../assets/down-arrow.png";
import { useTranslation } from "react-i18next";
import { req_Status } from "../utils/enums";

const PortalDropdown = ({
  options,
  selected,
  setSelected,
  thick,
  flex,
  errorText,
  onChange,
  page = "invoices",
  getData = null,
  nameInAr = false,
  disabled,
  titleCssClass,
  prefetch = false,
  allowSameSelect = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const toggleRef = useRef(null);
  const menuRef = useRef(null);
  const isFirstClick = useRef(true);
  const { t } = useTranslation("general");
  const [loading, setLoading] = useState({ status: req_Status.NULL });

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      // Check if click is outside both the dropdown toggle AND the menu
      const isOutsideDropdown =
        dropdownRef.current && !dropdownRef.current.contains(event.target);
      const isOutsideMenu = !event.target.closest(".dropdown-menu-portal");

      if (isOutsideDropdown && isOutsideMenu) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  // Update menu position when dropdown opens or window resizes
  useEffect(() => {
    const updatePosition = () => {
      if (isOpen && toggleRef.current) {
        const rect = toggleRef.current.getBoundingClientRect();
        const menuHeight = menuRef.current ? menuRef.current.offsetHeight : 300; // Fallback to max height
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;

        // Determine if dropdown should open upward
        const shouldOpenUpward =
          spaceBelow < menuHeight && spaceAbove > spaceBelow;
        setOpenUpward(shouldOpenUpward);

        // Calculate position based on direction
        const top = shouldOpenUpward
          ? rect.top + window.scrollY - menuHeight - 5 // Above the toggle
          : rect.bottom + window.scrollY + 5; // Below the toggle

        setMenuPosition({
          top: top,
          left: rect.left + window.scrollX,
          width: rect.width,
        });
      }
    };

    if (isOpen) {
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
    }

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen]);

  const toggleDropdown = async () => {
    if (disabled) return;

    setIsOpen(!isOpen);

    if (getData && isFirstClick.current) {
      setLoading((prev) => ({ ...prev, status: req_Status.PENDING }));
      const req = await getData();
      isFirstClick.current = false;

      if (req?.status === 200) {
        setLoading((prev) => ({ ...prev, status: req_Status.SUCCESS }));
      } else {
        setLoading((prev) => ({ ...prev, status: req_Status.FAILED }));
      }
    }
  };

  const handleItemClick = (item) => {
    if (selected?.id !== item.id && !allowSameSelect) setSelected(item, page);
    if (allowSameSelect) setSelected(item, page);
    if (onChange) {
      onChange(item);
    }
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      toggleDropdown();
    }
  };

  const showError = !selected?.value && errorText;

  useEffect(() => {
    if (prefetch && getData) {
      setLoading((prev) => ({ ...prev, status: req_Status.PENDING }));
      getData().then((req) => {
        isFirstClick.current = false;
        if (req?.status === 200) {
          setLoading((prev) => ({ ...prev, status: req_Status.SUCCESS }));
        } else {
          setLoading((prev) => ({ ...prev, status: req_Status.FAILED }));
        }
      });
    }
  }, [prefetch]);

  // Render the dropdown menu in a portal
  const renderMenu = () => {
    if (!isOpen) return null;

    return createPortal(
      <ul
        ref={menuRef}
        className={`dropdown-menu-portal ${openUpward ? "open-upward" : ""}`}
        style={{
          position: "absolute",
          top: `${menuPosition.top}px`,
          left: `${menuPosition.left}px`,
          width: `${menuPosition.width}px`,
        }}
      >
        {Array.isArray(options) &&
          options.map((option, index) => (
            <li
              key={index}
              className={`${titleCssClass} dropdown-item`}
              onClick={() => handleItemClick(option)}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleItemClick(option);
                }
              }}
            >
              {!nameInAr
                ? typeof option === "object"
                  ? t(option?.title) || option?.name
                  : option
                : typeof option === "object"
                ? t(option?.title) || option?.nameInAr
                : option}
            </li>
          ))}
      </ul>,
      document.body
    );
  };

  return (
    <div
      className={`c-dropdown ${flex ? "full-width-dropdown" : ""}`}
      ref={dropdownRef}
    >
      {showError && <div className="error-message">{t(errorText)}</div>}
      <div
        ref={toggleRef}
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        className={`toggle ${isOpen ? "opened" : ""} ${thick ? "thick" : ""}`}
        style={disabled ? { cursor: "not-allowed", opacity: 0.6 } : {}}
      >
        {!nameInAr ? (
          options && typeof selected === "object" && selected !== null ? (
            <p className="text">
              {selected?.title ? t(selected.title) : selected?.name}
            </p>
          ) : null
        ) : options && typeof selected === "object" && selected !== null ? (
          <p className="text">
            {selected?.title
              ? t(selected.title)
              : nameInAr
              ? selected?.nameInAr
              : selected?.name}
          </p>
        ) : null}
        <img
          src={arrowIcon}
          alt="Dropdown arrow"
          className={`arrow-icon ${isOpen ? "rotate180" : ""}`}
        />
      </div>

      {renderMenu()}
    </div>
  );
};

export default PortalDropdown;
